/**
 * These are all the times that are concidered active (green). These are all passes that are connected to a contestant,
 * and are connected to an actual result list. i.e has a StationRoleID
 */
import { RaceContestantTime } from '@/objects/generated';
import { defineStore, storeToRefs } from 'pinia';
import { useEventStoreRef } from './eventStore';
import { TimeDao } from '@/dao';
import { onUnmounted, watch } from 'vue';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { addToMapWithArray } from '@/utilities/arrayUtils';
import { compareDateTimes } from '@/utilities/dateutils';
import { ProfileHelper } from '@/utilities/profileHelper';

// TODO: Move this type to its own file
export type TimeValues = 'RaceContestantTimes' | 'ContestantTimes' | 'GpiReadings';

type RaceContestantTimeSubscriptionOptions = {
    triggerForExistingTimes?: boolean;
    timeTypes?: Array<'Time' | 'Deleted'>;
};

type RaceContestantTimeKey = string;
type RaceContestantId = number;
type ContestantId = number;
type StationRoleId = number;

type LoaderValueType = {
    /**
     * All times, with the Id as key
     *
     * These are unsorted
     */
    times: Map<RaceContestantTimeKey, RaceContestantTime>;

    /**
     * Contains a list of all times sorted by ClockTime ascending
     * This sort is guaranteed to be ascending by ClockTime
     */
    sortedTimes: RaceContestantTime[];

    /**
     * Map of RaceContestantID to a list of times
     *
     * These are unsorted
     */
    raceContestantTimesMap: Map<RaceContestantId, RaceContestantTime[]>;

    /**
     * Map of ContestantID to a list of times
     *
     * These are unsorted
     */
    contestantTimeMap: Map<ContestantId, RaceContestantTime[]>;

    /**
     * Map of station times
     *
     * StationID -> [Time]
     */
    lastActiveTimeOnStation: Map<StationRoleId, RaceContestantTime>;
};

export type RaceContestantTimeStore = ReturnType<typeof useRaceContestantTimeStore>;

export const useRaceContestantTimeStore = defineStore('racecontestanttimestore', () => {
    // We need to listen to changes to the event
    const { eventID, initpromise } = useEventStoreRef();

    /**
     * List of all the subscriptions for the racecontestanttimes
     */
    const registeredSubscriptions = new Set<[Function, RaceContestantTimeSubscriptionOptions]>();

    /**
     * A loader for the times
     */
    const raceContestantTimeLoader = new LoaderWithReset<LoaderValueType>({
        defaultValue: {
            times: new Map<RaceContestantTimeKey, RaceContestantTime>(),
            sortedTimes: [],
            raceContestantTimesMap: new Map<number, RaceContestantTime[]>(),
            contestantTimeMap: new Map<ContestantId, RaceContestantTime[]>(),
            lastActiveTimeOnStation: new Map<StationRoleId, RaceContestantTime>(),
        },
        loaderFunction: async () => {
            // This is ordered by Time so it should be in ascending order. But there is no guarantee!
            const times = await TimeDao.GetRaceContestantTimes(eventID.value);

            const value = {
                times: new Map<RaceContestantTimeKey, RaceContestantTime>(),
                sortedTimes: [],
                raceContestantTimesMap: new Map<number, RaceContestantTime[]>(),
                contestantTimeMap: new Map<ContestantId, RaceContestantTime[]>(),
                lastActiveTimeOnStation: new Map<StationRoleId, RaceContestantTime>(),
            };

            addTimesToLoaderValue(value, times);

            return value;
        },
        waitOnBeforeLoad: initpromise.value,
    });

    /**
     * Add the given times to the loader value
     * @param loaderValue
     * @param times
     */
    const addTimesToLoaderValue = (loaderValue: LoaderValueType, times: RaceContestantTime[], profileLabel?: string) => {
        const prof = ProfileHelper.start('RaceContestantTimeStore addTimesToLoaderValue');
        prof.mark('start');

        const raceContestantTimesMapKeys = new Set<RaceContestantId>();
        const contestantTimesMapKeys = new Set<ContestantId>();

        // Cutoff to decide if we should sort once or multiple times
        const sortOrderedListOnce = times.length > 20;

        const deletedStationRoleIds = new Set<StationRoleId>();

        times.forEach((t) => {
            // If its deleted and we already have it, remove it
            if (t.Status === 'Deleted' && raceContestantTimeLoader.Value.times.has(t.Id!)) {
                const timeToDelete = raceContestantTimeLoader.Value.times.get(t.Id!);
                raceContestantTimeLoader.Value.times.delete(t.Id!);

                // Remove the same time from the raceContestantTimesMap
                loaderValue.raceContestantTimesMap.set(timeToDelete!.RaceContestantID, [
                    ...loaderValue.raceContestantTimesMap.get(timeToDelete!.RaceContestantID)!.filter((rt) => rt.Id !== timeToDelete!.Id),
                ]);

                // Remove the same time from the contestantTimeMap
                loaderValue.contestantTimeMap.set(timeToDelete!.ContestantID, [
                    ...loaderValue.contestantTimeMap.get(timeToDelete!.ContestantID)!.filter((ct) => ct.Id !== timeToDelete!.Id),
                ]);

                // We iterate the sortedTimes to see if we need to remove it
                // Since we are deleting it it will maintain the sort order.
                for (const [index, existingTime] of loaderValue.sortedTimes.entries()) {
                    if (existingTime.Id === timeToDelete!.Id) {
                        loaderValue.sortedTimes.splice(index, 1);
                        break;
                    }
                }

                // Remove from lastActiveTimeOnStation if it was the last time
                // TODO: We need to update with the previous time if there is one since we now removed the last time
                // We iterate the the stationmap to see if the time to delete is there
                for (const [stationRoleId, existingTime] of loaderValue.lastActiveTimeOnStation) {
                    if (existingTime.Id === timeToDelete!.Id) {
                        loaderValue.lastActiveTimeOnStation.delete(stationRoleId);
                        deletedStationRoleIds.add(stationRoleId);
                        break;
                    }
                }

                return;
            }

            // Handling the sortedTimes list
            // We iterate from the back to add the time in the correct position
            if (!sortOrderedListOnce) {
                for (let i = loaderValue.sortedTimes.length - 1; i >= 0; i--) {
                    const existingTime = loaderValue.sortedTimes[i];
                    if (compareDateTimes(existingTime.ClockTime, t.ClockTime) <= 0) {
                        loaderValue.sortedTimes.splice(i + 1, 0, t);
                        break;
                    }
                }
            }

            loaderValue.times.set(t.Id!, t);

            // Add to the raceContestantTimesMap
            addToMapWithArray(loaderValue.raceContestantTimesMap, t.RaceContestantID, t);
            raceContestantTimesMapKeys.add(t.RaceContestantID); // For sorting

            // Add to the contestantTimeMap
            addToMapWithArray(loaderValue.contestantTimeMap, t.ContestantID, t);
            contestantTimesMapKeys.add(t.ContestantID);

            // Add to the stationRoleTimeMap
            // We need to check if the clock time is later than the existing one
            if (t.StationRoleID) {
                // Delete this time from other if it exists? ( StationRoleID - Lap - EtappeDeltakerID)
                for (const [stationRoleId, existingTime] of loaderValue.lastActiveTimeOnStation) {
                    if (existingTime.Id === t.Id) {
                        loaderValue.lastActiveTimeOnStation.delete(stationRoleId);
                        break;
                    }
                }

                const existingTime = loaderValue.lastActiveTimeOnStation.get(t.StationRoleID);
                if (!existingTime || existingTime.ClockTime < t.ClockTime) {
                    loaderValue.lastActiveTimeOnStation.set(t.StationRoleID, t);
                }
            }
        });

        prof.mark('forEach');

        // Iterate the raceContestantTimesMap keys and sort their times by ClockTime ascending
        raceContestantTimesMapKeys.forEach((raceContestantId) => {
            loaderValue.raceContestantTimesMap.get(raceContestantId)!.sort((a, b) => {
                let timeCompare = compareDateTimes(a.ClockTime, b.ClockTime);
                if (timeCompare === 0) {
                    timeCompare = a.Id!.localeCompare(b.Id!);
                }
                return timeCompare;
            });
        });

        prof.mark('sortRaceMap');

        // Iterate the contestantTimesMap keys and sort their times by ClockTime ascending
        contestantTimesMapKeys.forEach((contestantId) => {
            loaderValue.contestantTimeMap.get(contestantId)!.sort((a, b) => {
                let timeCompare = compareDateTimes(a.ClockTime, b.ClockTime);
                if (timeCompare === 0) {
                    timeCompare = a.Id!.localeCompare(b.Id!);
                }
                return timeCompare;
            });
        });

        prof.mark('sortContestantMap');

        if (sortOrderedListOnce) {
            // Finally sort the ordered list once
            loaderValue.sortedTimes.sort((a, b) => {
                let timeCompare = compareDateTimes(a.ClockTime, b.ClockTime);
                if (timeCompare === 0) {
                    timeCompare = a.Id!.localeCompare(b.Id!);
                }
                return timeCompare;
            });
        }

        prof.mark('sorted');

        if (deletedStationRoleIds.size > 0) {
            // We need to iterate the sortedTimes from the back and add the lastActiveTimeOnStation again
            for (let i = loaderValue.sortedTimes.length - 1; i >= 0; i--) {
                const time = loaderValue.sortedTimes[i];
                if (time.StationRoleID && deletedStationRoleIds.has(time.StationRoleID)) {
                    loaderValue.lastActiveTimeOnStation.set(time.StationRoleID, time);
                    // we remove the stationRoleId from the set since we have updated it
                    deletedStationRoleIds.delete(time.StationRoleID);
                    if (deletedStationRoleIds.size === 0) {
                        break;
                    }
                }
            }
        }

        prof.mark('done');
        prof.end();
    };

    /**
     * Reset the loaders
     */
    const reset = () => {
        raceContestantTimeLoader.Reset();
    };

    /**
     * This is a waitable function that makes sure we have loaded all the data we need
     */
    const ensureLoaded = async () => await raceContestantTimeLoader.Load();

    /**
     * Handle the times we get on the signalr subscription
     * @param times The times received from the signalr subscription
     */
    const handleRaceContestantTimes = async (times: RaceContestantTime[]) => {
        // Debug
        // times.forEach((t) => {
        //     console.log(`Received raceContestantTime with id ${t.Id} and time ${t.ClockTime} and status ${t.Status} from signalR`);
        // });
        // End debug

        const prof = ProfileHelper.start(`RaceContestantTimeStore handleRaceContestantTimes[${times.length}]`);

        prof.mark('start');

        // Make sure we load the times from the server before we handle our first signalr time.
        // If we dont do this, the load will overwrite the time we just got when its loaded
        await ensureLoaded();

        prof.mark('ensureLoaded');

        // Add the times to the loader dataset
        addTimesToLoaderValue(raceContestantTimeLoader.Value, times);

        prof.mark('addTimesToLoaderValue');

        // Loop through all the subscriptions and call them with the new times
        for (const [subscription, options] of registeredSubscriptions) {
            // Notify the subscription of all received times of the types they have subscribed to.
            // Time includes all statuses that are not Deleted, eg. DNF, Lap, etc.
            subscription(
                times.filter(
                    (t) =>
                        (t.Status !== 'Deleted' && options.timeTypes?.includes('Time')) ||
                        (t.Status === 'Deleted' && options.timeTypes?.includes('Deleted')),
                ),
            );
        }
        prof.mark('subscriptionsNotified');
        prof.end();
    };

    /**
     * The unsubscribe function for the time subscription
     */
    let raceContestantTimeUnsubscribeFunction: (() => Promise<void>) | null = null;

    const startSignalRSubscription = async () => {
        if (raceContestantTimeUnsubscribeFunction) {
            await raceContestantTimeUnsubscribeFunction();
        }

        raceContestantTimeUnsubscribeFunction = await signalrSubscribe.RaceContestantTime(
            {
                EventID: eventID.value,
            },
            async (at) => await handleRaceContestantTimes(at),
        );
    };

    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            startSignalRSubscription();
        },
    );

   

    startSignalRSubscription();

    /**
     * Subscribe to the RaceContestantTimes
     * @param callback The callback to be invoked when we get a new time
     * @param options Options for the subscription
     */
    const subscribeToRaceContestantTimes = (
        callback: (times: RaceContestantTime[]) => void,
        options: RaceContestantTimeSubscriptionOptions = { triggerForExistingTimes: true, timeTypes: ['Time', 'Deleted'] }, // Note: Times with any other status is considered to be a Time
    ) => {
        // Default both Time and Deleted if timetypes has not been defined
        if (!options.timeTypes) {
            options.timeTypes = ['Time', 'Deleted'];
        }

        const callbackAndOptionsTuple = [callback, options] as [Function, RaceContestantTimeSubscriptionOptions];
        // Add the callback to the list of subscriptions
        registeredSubscriptions.add(callbackAndOptionsTuple);

        // If triggerForExistingTimes is true we need to trigger the callback with the existing times
        if (options.triggerForExistingTimes && options.timeTypes.includes('Time')) {
            ensureLoaded().then(() => {
                callback(Array.from(raceContestantTimeLoader.Value.times.values()));
            });
        }

        return () => {
            registeredSubscriptions.delete(callbackAndOptionsTuple);
        };
    };

    return {
        /**
         * Ensure that  all the data will be loaded from the server
         * this is a waitable function
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Add a subscription to receive times
         */
        SubscribeToRaceContestantTimes: subscribeToRaceContestantTimes,

        /**
         * Returns all times currently loaded as an array
         * @remarks This is not sorted in any way
         */
        GetAllTimes: () => Array.from(raceContestantTimeLoader.Value.times.values()),

        /**
         * Gets all RaceContestantIds that has a time
         */
        GetRaceContestantIdsWithTimes: () => Array.from(raceContestantTimeLoader.Value.raceContestantTimesMap.keys()),

        /**
         * Get a list of times for a given raceContestantId
         * @param raceContestantId
         */
        GetTimesForRaceContestantId: (raceContestantId: RaceContestantId) =>
            raceContestantTimeLoader.Value.raceContestantTimesMap.get(raceContestantId) || [],

        /**
         * Gets all the RaceContestantTimes for a given contestantId
         *
         * So this can give you times for several RaceContestants
         * @param contestantId The contestantId we want times for
         * @returns An array of RaceContestantTimes
         */
        GetTimesForContestantId: (contestantId: ContestantId) => raceContestantTimeLoader.Value.contestantTimeMap.get(contestantId) || [],

        /**
         * We get all times for a given stationRoleId
         * @remarks This should be ordered ASC by time, but there is no guarantee
         * @param stationRoleId
         * @returns
         */
        GetLastTimeForStationRoleId: (stationRoleId: StationRoleId) =>
            raceContestantTimeLoader.Value.lastActiveTimeOnStation.get(stationRoleId) ?? null,
    };
});

export const useRaceContestantTimeStoreRef = () => storeToRefs(useRaceContestantTimeStore());

// helper composable to handle lifetime of a subscription to the racecontestanttimes. This is not required to use the store, but it makes it easier to subscribe to the times in a component and automatically unsubscribe when the component is unmounted.
export const useRaceContestantTimesSubscription = (
    callback: (times: RaceContestantTime[]) => void,
    options?: RaceContestantTimeSubscriptionOptions,
) => {
    const store = useRaceContestantTimeStore();

    const cleanup = store.SubscribeToRaceContestantTimes(callback, options);

    onUnmounted(() => {
        cleanup();
    });
};
